import * as React from 'react';
import {InView} from 'react-intersection-observer';
import classNames from 'classnames';
import {Flex} from 'components/UIKit/Flex';
import {Icon} from 'components/UIKit/Icon';
import {Text} from 'components/UIKit/Text';
import {NOOP} from 'components/UIKit/utilities';
import {makePictureData} from './utilities';
import {ImageOptions, PictureData} from './types';
import style from './Picture.scss';

type ImageProps = JSX.IntrinsicElements['img'];

export interface PictureProps {
  imageOptions: ImageOptions[];
  pictureData?: PictureData;
  imageUrl?: string;
  onLoad?: () => any;
  alt: string;
  imageClassName?: string;
  pictureClassName?: string;
  lazyload?: boolean;
  rootMargin?: string;
}

export class Picture extends React.Component<PictureProps & ImageProps> {
  image: React.RefObject<HTMLImageElement>;

  constructor(props) {
    super(props);
    this.image = React.createRef();
  }

  componentDidMount() {
    const {onLoad = NOOP} = this.props;
    // Beacause SSR images don't fire an onLoad event, we run it if the image is
    // complete to ensure it fires.
    if (this.image.current && this.image.current.complete) {
      onLoad();
    }
  }

  errorState = () => {
    return (
      <Flex alignItems="center" className={style.NoImageContainer}>
        <Icon icon="alertTriangle" size={40} className={style.NoImage} aria-hidden />
        <Text text="No imageUrl or pictureData provided" />
      </Flex>
    );
  };

  render() {
    const {
      imageUrl,
      alt,
      imageOptions,
      pictureData,
      onLoad = NOOP,
      imageClassName,
      pictureClassName,
      lazyload = false,
      rootMargin = '200px',
      ...restProps
    } = this.props;

    let pictureSourceData;

    if (!imageUrl && !pictureData) {
      return this.errorState();
    }

    if (pictureData) {
      pictureSourceData = pictureData;
    } else if (imageUrl) {
      pictureSourceData = makePictureData(imageUrl, imageOptions);
    }

    const {sources, fallback} = pictureSourceData;

    return lazyload ? (
      <InView triggerOnce rootMargin={rootMargin}>
        {({inView, ref}) => (
          <>
            {!imageUrl && !pictureData && this.errorState()}
            <picture ref={ref} className={classNames(style.Picture, pictureClassName)}>
              {inView &&
                sources.map((source) => (
                  <source key={source.srcset} srcSet={source.srcset} media={source.media} />
                ))}

              <img
                ref={this.image}
                src={inView ? fallback : ''}
                alt={alt}
                onLoad={onLoad}
                className={classNames(style.LazyloadImage, imageClassName, {
                  [style.Loaded]: inView,
                })}
                {...restProps}
              />
            </picture>
          </>
        )}
      </InView>
    ) : (
      <>
        {!imageUrl && !pictureData && this.errorState()}
        <picture className={classNames(style.Picture, pictureClassName)}>
          {sources.map((source) => (
            <source key={source.srcset} srcSet={source.srcset} media={source.media} />
          ))}
          <img
            ref={this.image}
            src={fallback}
            alt={alt}
            onLoad={onLoad}
            className={imageClassName}
            {...restProps}
          />
        </picture>
      </>
    );
  }
}
